import { Injectable, Injector, Type, inject } from '@angular/core';
import { CalendarServerType, CalendarSyncEventIdentityInput, CalendarsService } from '@cue/calendars';
import { CalendarSyncLoadEventsResponse } from '@cue/calendar-sync';
import { Observable, defer, forkJoin, map, of, shareReplay, tap } from 'rxjs';

type CalendarSyncCalendarsServiceRegistration = {
  type: CalendarServerType;
  providerKeys: readonly string[];
  serverTypeIds: readonly number[];
  load: () => Observable<Type<CalendarsService>>;
};

const providerServiceRegistrations: readonly CalendarSyncCalendarsServiceRegistration[] = [
  {
    type: 'o365',
    providerKeys: ['o365', 'microsoft365'],
    serverTypeIds: [1],
    load: () => defer(() => import('@cue/calendars/o365')).pipe(map((module) => module.O365CalendarService)),
  },
  {
    type: 'ews',
    providerKeys: ['ews', 'exchange'],
    serverTypeIds: [2],
    load: () => defer(() => import('@cue/calendars/ews')).pipe(map((module) => module.EWSCalendarService)),
  },
  {
    type: 'gspace',
    providerKeys: ['gspace', 'google-workspace', 'googleworkspace'],
    serverTypeIds: [4],
    load: () => defer(() => import('@cue/calendars/gspace')).pipe(map((module) => module.GSpaceCalendarService)),
  },
  {
    type: 'touchone-calendar',
    providerKeys: ['touchone-calendar', 'cue-calendar', 'cuecalendar'],
    serverTypeIds: [5],
    load: () => defer(() => import('@cue/calendars/touchone')).pipe(map((module) => module.TouchoneCalendarService)),
  },
];

@Injectable({
  providedIn: 'root',
})
export class CalendarSyncCalendarsServiceResolver {
  private readonly injector = inject(Injector);
  private readonly defaultCalendarsService = inject(CalendarsService);
  private readonly resolvedServices = new Map<CalendarServerType, CalendarsService>();
  private readonly resolvingServices = new Map<CalendarServerType, Observable<CalendarsService>>();

  ensureServicesForResponse(response: CalendarSyncLoadEventsResponse): Observable<CalendarSyncLoadEventsResponse> {
    const providerTypes = this.getProviderTypesFromResponse(response);

    if (providerTypes.length === 0) {
      return of(response);
    }

    return forkJoin(providerTypes.map((type) => this.resolveService(type))).pipe(map(() => response));
  }

  getCalendarSyncEventDiscriminator(event: CalendarSyncEventIdentityInput): string {
    return this.getServiceForEvent(event).getCalendarSyncEventDiscriminator(event);
  }

  private getProviderTypesFromResponse(response: CalendarSyncLoadEventsResponse): CalendarServerType[] {
    const types = new Set<CalendarServerType>();

    for (const resource of response.resources ?? []) {
      const resourceType = this.resolveProviderType({
        providerKey: resource.providerKey,
        serverTypeId: resource.serverTypeId,
      });

      if (resourceType) {
        types.add(resourceType);
      }

      for (const event of resource.events ?? []) {
        const eventType = this.resolveProviderType(event);
        if (eventType) {
          types.add(eventType);
        }
      }
    }

    return [...types];
  }

  private getServiceForEvent(event: CalendarSyncEventIdentityInput): CalendarsService {
    const providerType = this.resolveProviderType(event);

    if (!providerType) {
      return this.defaultCalendarsService;
    }

    return this.resolvedServices.get(providerType) ?? this.defaultCalendarsService;
  }

  private resolveService(type: CalendarServerType): Observable<CalendarsService> {
    const resolved = this.resolvedServices.get(type);
    if (resolved) {
      return of(resolved);
    }

    if (type === this.defaultCalendarsService.type) {
      this.resolvedServices.set(type, this.defaultCalendarsService);
      return of(this.defaultCalendarsService);
    }

    const resolving = this.resolvingServices.get(type);
    if (resolving) {
      return resolving;
    }

    const registration = this.getRegistrationByType(type);
    if (!registration) {
      return of(this.defaultCalendarsService);
    }

    const loadedService = registration.load().pipe(
      map((serviceType) => this.injector.get(serviceType)),
      tap((service) => this.resolvedServices.set(type, service)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.resolvingServices.set(type, loadedService);
    return loadedService;
  }

  private resolveProviderType(event: CalendarSyncEventIdentityInput): CalendarServerType | null {
    const providerKey = this.normalizeProviderKey(event.providerKey ?? event.serverType);
    if (providerKey) {
      const providerRegistration = providerServiceRegistrations.find((registration) =>
        registration.providerKeys.includes(providerKey)
      );

      if (providerRegistration) {
        return providerRegistration.type;
      }
    }

    if (event.serverTypeId != null) {
      const serverTypeRegistration = providerServiceRegistrations.find((registration) =>
        registration.serverTypeIds.includes(event.serverTypeId!)
      );

      if (serverTypeRegistration) {
        return serverTypeRegistration.type;
      }
    }

    return null;
  }

  private normalizeProviderKey(value: string | number | null | undefined): string | null {
    if (value == null) {
      return null;
    }

    return String(value).trim().toLowerCase();
  }

  private getRegistrationByType(type: CalendarServerType): CalendarSyncCalendarsServiceRegistration | undefined {
    return providerServiceRegistrations.find((registration) => registration.type === type);
  }
}
